// ===========================================
// QUICK ACTIONS — the three things you actually do from an account
// ===========================================
// "New order like last" drafts a copy of their most recent order (a DRAFT —
// nothing ships without you), "Log a call" drops you into the note form, and
// "Set standing weekly" turns the last order into the weekly rhythm. Each
// result shows up in the timeline as a derived event once the data refreshes.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CopyPlus, Phone, Repeat } from 'lucide-react';
import { wholesaleApi } from '../../services/api';
import { WEEKDAY_NAMES } from '../../utils/cpg';

export default function QuickActions({ account, say, oops, onLogCall }: {
  account: any;
  say: (tone: 'success' | 'error', text: string) => void;
  oops: (e: any, fallback: string) => void;
  onLogCall: () => void;
}) {
  const qc = useQueryClient();
  const lastOrder = account.stats?.orders?.[0] || null;
  // Only lines linked to a catalog product can be re-ordered or made standing.
  const reorderLines = (lastOrder?.items || [])
    .filter((it: any) => it.menuItemId && (parseFloat(String(it.quantity)) || 0) > 0)
    .map((it: any) => ({
      menuItemId: it.menuItemId,
      quantity: parseFloat(String(it.quantity)),
      ...(it.unitPriceCents > 0 ? { unitPriceCents: it.unitPriceCents } : {}),
    }));
  const canReorder = reorderLines.length > 0;

  const newLikeLast = useMutation({
    mutationFn: () => wholesaleApi.createOrder({
      customerId: account.id,
      items: reorderLines,
      notes: `Drafted from Accounts — same items as ${lastOrder.orderNumber}.`,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['wholesale-orders'] });
      const num = res.data?.data?.orderNumber;
      say('success', `${num ? `${num} drafted` : 'Order drafted'} with the same items as ${lastOrder.orderNumber}. It's a draft in Wholesale — nothing ships until you confirm it.`);
    },
    onError: (e: any) => oops(e, "Couldn't draft the order."),
  });

  const makeStanding = useMutation({
    mutationFn: () => wholesaleApi.makeStandingOrder(lastOrder.id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
      qc.invalidateQueries({ queryKey: ['hub-pipeline'] });
      const day = res.data?.data?.weekdayName || WEEKDAY_NAMES[res.data?.data?.standingWeekly?.weekday] || 'each week';
      say('success', `Standing weekly set from ${lastOrder.orderNumber} — every ${day}. Each week's order arrives as a draft; nothing confirms without you.`);
    },
    onError: (e: any) => oops(e, "Couldn't set the standing order."),
  });

  const noOrderTip = 'Needs a past order with catalog-linked items.';

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick actions</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => newLikeLast.mutate()} disabled={!canReorder || newLikeLast.isPending}
          className="btn btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-50"
          title={canReorder ? `Draft a new order with the same items as ${lastOrder.orderNumber}` : noOrderTip}>
          <CopyPlus className="w-4 h-4" /> {newLikeLast.isPending ? 'Drafting…' : 'New order like last'}
        </button>
        <button onClick={onLogCall} className="btn btn-secondary text-sm flex items-center gap-1.5"
          title="Jump to the note form below">
          <Phone className="w-4 h-4" /> Log a call
        </button>
        <button onClick={() => makeStanding.mutate()} disabled={!canReorder || makeStanding.isPending}
          className="btn btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-50"
          title={canReorder
            ? `${account.standingWeekly ? 'Replace the current standing order with' : 'Repeat'} ${lastOrder.orderNumber}'s items weekly — each week drafts, you confirm`
            : noOrderTip}>
          <Repeat className="w-4 h-4" /> {makeStanding.isPending ? 'Setting…' : account.standingWeekly ? 'Reset standing from last order' : 'Set standing weekly from last order'}
        </button>
      </div>
    </div>
  );
}
